// Live progress for long-running server tasks (AI derives, ingests…).
//
// Two data shapes, one component:
//  - `progress` — the slim field the backend writes onto the work doc
//    (phase/pct/eta/cost). Enough for compact inline bars in lists.
//  - `task` — the full registry doc at orgs/{org}/tasks/{id}: step checklist
//    (done/active/pending with timing), the actual backend log stream, token
//    spend. Rendered as a terminal-style live feed.
//
// Both stream via Firestore onSnapshot upstream; a 1s tick keeps elapsed/ETA
// counting between server writes.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import FitText from './FitText';
import './TaskProgress.css';

function fmtDur(ms) {
  if (ms == null) return '—';
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

const fmtTokens = (n) => (!n ? '0' : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));
const fmtClock = (at) => new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

const STEP_ICON = { done: '✓', active: '›', pending: '○', failed: '✕' };

export default function TaskProgress({ progress, task, compact = false, hideSteps = false }) {
  const [, setTick] = useState(0);
  const logRef = useRef(null);
  const p = task || progress;
  const logCount = task?.logs?.length || 0;

  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  // Keep the log pane pinned to the newest line as entries stream in.
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logCount]);

  if (!p) return null;

  const now = Date.now();
  const running = task ? task.status === 'running' : true;
  const failed = (task ? task.status === 'failed' : p.phase === 'failed');
  const elapsed = (task?.finishedAt || (running ? now : p.updatedAt)) - (p.startedAt || now);
  const remaining = running && p.etaMs != null ? Math.max(0, p.etaMs - (now - (p.updatedAt || now))) : null;
  const pct = failed ? null : task?.status === 'done' ? 100 : p.pct ? Math.min(100, Math.round(p.pct * 100)) : null;

  return (
    <div className={`task-progress ${compact ? 'is-compact' : ''} ${failed ? 'is-failed' : ''}`}>
      <div className="task-progress-head">
        {/* Phase only — every surface that renders this already shows the
            task/document title nearby; repeating it here just pushed the
            actual activity off the end of the line. */}
        <FitText className="task-progress-phase" text={p.phase} />
        <span className="task-progress-meta u-tabular" title={p.etaBasis ? `ETA basis: ${p.etaBasis}` : undefined}>
          {fmtDur(elapsed)} elapsed
          {remaining != null && ` · ~${fmtDur(remaining)} left`}
          {p.costUsd > 0 && ` · $${p.costUsd.toFixed(2)}`}
          {!compact && p.tokensIn > 0 && ` · ${fmtTokens(p.tokensIn)} in / ${fmtTokens(p.tokensOut)} out`}
          {p.callsTotal != null && ` · call ${p.calls}/${p.callsTotal}`}
          {!compact && !hideSteps && (task?.id || p.taskId) && (
            <>
              {' · '}
              <Link to={`/activity/${task?.id || p.taskId}`} title="Watch the run in the processing cockpit — live knowledge graph + vector retrieval">
                cockpit
              </Link>
            </>
          )}
        </span>
      </div>
      <div className="task-progress-bar" role="progressbar" aria-valuenow={pct ?? undefined} aria-valuemin={0} aria-valuemax={100}>
        <div className={`task-progress-fill ${pct == null && !failed ? 'is-indeterminate' : ''}`} style={pct != null ? { width: `${pct}%` } : undefined} />
      </div>
      {!compact && !hideSteps && task?.steps?.length > 0 && (
        <div className="task-progress-steps">
          {task.steps.map((s) => (
            <div key={s.id} className={`task-progress-step is-${s.status}`}>
              <span className="task-progress-step-icon">{STEP_ICON[s.status] || '○'}</span>
              <span className="task-progress-step-label">{s.label}</span>
              <span className="task-progress-step-ms u-tabular">
                {s.status === 'done' && s.ms != null ? fmtDur(s.ms) : s.status === 'active' && s.startedAt ? fmtDur(now - s.startedAt) : ''}
              </span>
            </div>
          ))}
        </div>
      )}
      {!compact && task?.logs?.length > 0 && (
        <div className="task-progress-logs" ref={logRef}>
          {task.logs.map((l) => (
            <div key={l.at} className="task-progress-log">
              <span className="task-progress-log-t u-tabular">{fmtClock(l.at)}</span>
              {l.line}
            </div>
          ))}
          {running && <div className="task-progress-log task-progress-cursor">▋</div>}
        </div>
      )}
      {compact && task?.logs?.length > 0 && (
        <div className="task-progress-log u-xs u-muted">
          <span className="task-progress-log-t u-tabular">{fmtClock(task.logs[task.logs.length - 1].at)}</span>
          <FitText style={{ flex: 1 }} text={task.logs[task.logs.length - 1].line} />
        </div>
      )}
    </div>
  );
}
